import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Archive,
  Copy as CopyIcon,
  Download,
  Plus,
  ShieldX,
  Check,
  ChevronsUpDown,
  Database,
  HardDrive,
  Search,
  RotateCcw,
} from "lucide-react";

import { RestoreApi, AccountsApi, ServersApi, type AccountRow, type RestoreRow, type RecommendedSet } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { formatBytes, num, timeAgo, jalaliDateTimeCompact } from "@/lib/utils";

export function RestorePage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["restores"],
    queryFn: () => RestoreApi.list(),
    refetchInterval: 5_000,
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Archive className="size-5 text-primary" />
            {fa.restore.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            بازیابی اکانت‌ها از روی اسنپ‌شات‌های آف‌سایت — دانلود لینک امضاشده یا اجرای مستقیم روی سرور cPanel هدف.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" />
          {fa.restore.newRestore}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            مجموع: <span className="text-foreground">{num((data ?? []).length)}</span> درخواست
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="py-10 px-5 text-center space-y-3">
              <Archive className="size-8 mx-auto text-muted-foreground/40" />
              <div className="text-sm text-muted-foreground">
                هنوز هیچ درخواست دانلودی وجود ندارد.
              </div>
              <div className="text-xs text-muted-foreground/80 max-w-md mx-auto leading-relaxed">
                این صفحه فهرست لینک‌های دانلود فعالِ مشتری‌هاست — نه فهرست بکاپ‌ها.
                برای ساختن یک لینک دانلود از یک بکاپ موجود،
                از تب <span className="text-foreground font-medium">«اسنپ‌شات‌ها»</span>
                {" "}روی دکمه‌ی ⬇ کنار هر ردیف کلیک کنید،
                یا روی {" "}<span className="text-foreground font-medium">«بازیابی جدید»</span>{" "}
                بالا سمت چپ بزنید تا ویزارد انتخاب اکانت باز شود.
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>اکانت</TableHead>
                  <TableHead>سرور</TableHead>
                  <TableHead>نوع</TableHead>
                  <TableHead>محتوا</TableHead>
                  <TableHead>{fa.common.state}</TableHead>
                  <TableHead>تاریخ بکاپ</TableHead>
                  <TableHead>حجم</TableHead>
                  <TableHead>ایجاد لینک</TableHead>
                  <TableHead className="w-32">اقدامات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((r) => <RestoreRowItem key={r.id} r={r} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewRestoreDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["restores"] });
          setOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Human label for what a restore artifact contained.
 *
 * "کامل" is reserved for files + at least one database — the thing that
 * actually reconstructs a site. Files without a database is NOT full, and
 * saying so plainly is the whole point: that combination is what left
 * liimasho restorable-in-name-only for a year.
 */
function restoreContents(r: RestoreRow): string {
  const c = r.contents;
  if (!c) return "—";
  const dbs = c.dbs?.length ?? 0;
  if (c.files && dbs > 0) return dbs === 1 ? "کامل (فایل + ۱ دیتابیس)" : `کامل (فایل + ${toFa(dbs)} دیتابیس)`;
  if (c.files) return "فقط فایل‌ها — بدون دیتابیس";
  if (dbs > 0) return dbs === 1 ? `فقط دیتابیس (${c.dbs![0]})` : `فقط دیتابیس (${toFa(dbs)} عدد)`;
  if (c.meta) return "فقط اطلاعات اکانت";
  return "—";
}

const toFa = (n: number) => n.toLocaleString("fa-IR");

function RestoreRowItem({ r }: { r: RestoreRow }) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => RestoreApi.revoke(r.id),
    onSuccess: () => { toast.success("درخواست ابطال شد"); qc.invalidateQueries({ queryKey: ["restores"] }); },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const retry = useMutation({
    mutationFn: () => RestoreApi.retry(r.id),
    onSuccess: () => {
      toast.success("درخواست جدید ساخته شد — وضعیت را دنبال کنید");
      qc.invalidateQueries({ queryKey: ["restores"] });
    },
    onError: (e: Error) => toast.error("ری‌ترای ناموفق", { description: e.message }),
  });

  const isFailed = r.state === "failed" || r.state === "expired" || r.state === "revoked";

  const stateBadge =
    r.state === "ready" ? "success"
    : r.state === "failed" ? "destructive"
    : r.state === "preparing" || r.state === "pending" ? "default"
    : r.state === "revoked" || r.state === "expired" ? "outline"
    : "secondary";

  const fullUrl = r.downloadUrl
    ? r.downloadUrl.startsWith("http")
      ? r.downloadUrl
      : `${window.location.protocol}//${window.location.host}${r.downloadUrl}`
    : "";

  return (
    <TableRow>
      <TableCell className="lat-num">{r.accountUser ?? "—"}</TableCell>
      <TableCell>{r.serverName ?? "—"}</TableCell>
      <TableCell className="text-xs">
        {r.targetType === "download_link" ? "لینک دانلود"
          : r.targetType === "cpanel_homedir" ? "homedir مبدأ"
          : "بازگردانی کامل"}
      </TableCell>
      {/*
        WHAT was in the artifact, as opposed to HOW it was delivered (the
        column to the right of this one). An operator handing a customer
        several links needs to say which one is the whole account and which
        is only the databases. Null for links built before `contents` was
        recorded — that composition is not recoverable, so it shows "—"
        rather than a guess.
      */}
      <TableCell className="text-xs whitespace-nowrap">{restoreContents(r)}</TableCell>
      <TableCell>
        <div className="space-y-1">
          <Badge variant={stateBadge as never}>
            {r.state === "ready" && r.targetType === "cpanel_full"
              ? fa.restore.appliedFull
              : r.state === "ready" && r.targetType === "cpanel_homedir"
                ? fa.restore.appliedHomedir
                : fa.restore.states[r.state]}
          </Badge>
          {isFailed && r.errorMessage && (
            <p className="text-[11px] text-destructive leading-snug max-w-xs" title={r.errorMessage}>
              {r.errorMessage.length > 80 ? r.errorMessage.slice(0, 80) + "…" : r.errorMessage}
            </p>
          )}
        </div>
      </TableCell>
      {/*
        The date of the BACKUP, not of the request. A request stores one
        "primary" snapshot whose `kind` varies by how the bundle was built
        (files for one, meta for another), so the kind is an implementation
        detail and is deliberately not shown — every snapshot in one backup
        shares its timestamp, which is the part the operator needs to tell
        the customer which link is which.
      */}
      <TableCell className="tabular whitespace-nowrap">
        {r.snapshotAt ? jalaliDateTimeCompact(r.snapshotAt) : "—"}
      </TableCell>
      <TableCell className="tabular">{r.sizeBytes != null ? formatBytes(r.sizeBytes) : "—"}</TableCell>
      <TableCell className="text-muted-foreground">{timeAgo(r.createdAt)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1 justify-end">
          {isFailed && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-amber-700 dark:text-amber-400 border-amber-400 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:text-amber-800 dark:hover:text-amber-300"
                title="تلاش مجدد با آخرین اسنپ‌شات‌ها"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                <RotateCcw className="size-3.5" />
                تلاش مجدد
              </Button>
          )}
          {r.state === "ready" && r.downloadUrl && (
            <>
              <Button asChild variant="ghost" size="sm" title="دانلود">
                <a href={r.downloadUrl} target="_blank" rel="noreferrer">
                  <Download className="size-4" />
                </a>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title={fa.restore.copyUrl}
                onClick={() => {
                  navigator.clipboard.writeText(fullUrl);
                  toast.success("لینک کپی شد");
                }}
              >
                <CopyIcon className="size-4" />
              </Button>
            </>
          )}
          {(r.state === "pending" || r.state === "preparing" ||
            (r.state === "ready" && r.targetType === "download_link")) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              title={fa.restore.revoke}
              onClick={() => { if (confirm("درخواست ابطال شود؟")) revoke.mutate(); }}
            >
              <ShieldX className="size-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function NewRestoreDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [accountId, setAccountId] = useState<string>("");
  const [selectedAccountCache, setSelectedAccountCache] = useState<AccountRow | null>(null);
  const [restoreKind, setRestoreKind] = useState<"full" | "db_only">("full");
  const [targetType, setTargetType] = useState<"download_link" | "cpanel_homedir" | "cpanel_full">(
    "download_link"
  );
  const [expiresInHours, setExpiresInHours] = useState(72);
  const [targetServerId, setTargetServerId] = useState<string>("");
  const [targetUser, setTargetUser] = useState<string>("");
  // Which restore point (backup date) to restore. "__latest__" = auto-pick
  // the freshest valid set (the historical default); otherwise the ISO
  // timestamp of the chosen anchor snapshot, sent to the API as `asOf`.
  const LATEST = "__latest__";
  const [pointAsOf, setPointAsOf] = useState<string>(LATEST);

  // Reset all fields when the dialog is re-opened so a previous selection
  // doesn't bleed into the next request.
  useEffect(() => {
    if (open) {
      setAccountId("");
      setSelectedAccountCache(null);
      setTargetServerId("");
      setTargetUser("");
      setRestoreKind("full");
      setPointAsOf(LATEST);
    }
  }, [open]);

  // A different account, or switching full↔db-only (which changes the anchor
  // snapshot kind), invalidates a previously pinned point — fall back to auto.
  useEffect(() => {
    setPointAsOf(LATEST);
  }, [accountId, restoreKind]);

  const serversQuery = useQuery({
    queryKey: ["servers-picker"],
    queryFn: () => ServersApi.list(),
    enabled: open,
  });

  // All snapshots for the account, used to build the list of selectable
  // restore points (one per backup run).
  const snapshotsQuery = useQuery({
    queryKey: ["restore-snapshots", accountId],
    queryFn: () => RestoreApi.snapshots(accountId),
    enabled: !!accountId,
    retry: false,
  });

  // Anchor restore points on the `files` snapshots for a full restore (each
  // is a distinct site version), or on `db_meta`/`db` for a DB-only restore.
  const anchorKind = restoreKind === "db_only" ? "db_meta" : "files";
  const restorePoints = (() => {
    const snaps = snapshotsQuery.data ?? [];
    let pts = snaps.filter((s) => s.kind === anchorKind && s.valid);
    if (pts.length === 0 && restoreKind === "db_only") {
      pts = snaps.filter((s) => s.kind === "db" && s.valid);
    }
    // Newest first, de-duplicated by timestamp (db-only fallback can have one
    // row per database at the same instant).
    const seen = new Set<string>();
    return pts
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .filter((s) => {
        if (seen.has(s.createdAt)) return false;
        seen.add(s.createdAt);
        return true;
      });
  })();

  const recommendQuery = useQuery({
    queryKey: ["restore-recommend", accountId, pointAsOf],
    queryFn: () => RestoreApi.recommend(accountId, pointAsOf === LATEST ? undefined : pointAsOf),
    enabled: !!accountId,
    retry: false,
  });

  // For a DB-only restore, strip out files+meta from the recommended set
  // so the cpmove tarball is small and the restore is fast.
  const effectiveSnapshotIds = (): RecommendedSet | null => {
    if (!recommendQuery.data) return null;
    if (restoreKind === "db_only") {
      return {
        db: recommendQuery.data.db,
        db_create: recommendQuery.data.db_create,
        db_meta: recommendQuery.data.db_meta,
      };
    }
    return recommendQuery.data;
  };

  const create = useMutation({
    mutationFn: () => {
      if (!accountId) throw new Error("اکانت مشخص نشده");
      const set = effectiveSnapshotIds();
      if (!set) throw new Error("اسنپ‌شاتی برای بازیابی پیدا نشد");
      if (restoreKind === "db_only" && !set.db && !set.db_meta) {
        throw new Error("این اکانت بکاپ دیتابیس ندارد");
      }
      if (targetType === "cpanel_full" && !targetServerId) {
        throw new Error("سرور cPanel هدف انتخاب نشده");
      }
      return RestoreApi.create({
        accountId,
        snapshotIds: set,
        targetType,
        ...(targetType === "download_link"
          ? { expiresInHours }
          : {}),
        ...(targetType === "cpanel_full" ? { targetServerId } : {}),
        ...(targetType !== "download_link" && targetUser ? { targetUser } : {}),
      });
    },
    onSuccess: () => {
      toast.success("درخواست بازیابی ساخته شد", {
        description: "وضعیت آن را در همین صفحه دنبال کنید.",
      });
      onCreated();
      setAccountId("");
      setSelectedAccountCache(null);
      setTargetServerId("");
      setTargetUser("");
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{fa.restore.newRestore}</DialogTitle>
          <DialogDescription>
            مجموعه‌ی اسنپ‌شات‌ها به‌صورت خودکار از آخرین مجموعه‌ی معتبر انتخاب می‌شود. بعد از تأیید،
            ایجنت سرور مبدأ <span dir="ltr">cpmove-&lt;user&gt;.tar.gz</span> را بازسازی می‌کند.
          </DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-3"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="grid gap-2">
            <Label>{fa.restore.pickAccount}</Label>
            <AccountCombobox
              servers={serversQuery.data?.items ?? []}
              selectedAccountId={accountId}
              selectedAccountCache={selectedAccountCache}
              snapshotKind={restoreKind === "db_only" ? "db" : "files"}
              onSelect={(a) => {
                setAccountId(a.id);
                setSelectedAccountCache(a);
              }}
            />
          </div>

          {/* Restore kind toggle — full or DB-only */}
          <div className="grid gap-2">
            <Label>نوع بازیابی</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-start transition-colors " +
                  (restoreKind === "full"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-muted/50")
                }
                onClick={() => setRestoreKind("full")}
              >
                <HardDrive className="size-4" />
                <div>
                  <div className="font-medium">بازیابی کامل</div>
                  <div className="text-[10px] text-muted-foreground">
                    homedir + meta + همهٔ دیتابیس‌ها
                  </div>
                </div>
                {restoreKind === "full" && <Check className="size-4 ms-auto" />}
              </button>
              <button
                type="button"
                className={
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-start transition-colors " +
                  (restoreKind === "db_only"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-muted/50")
                }
                onClick={() => setRestoreKind("db_only")}
              >
                <Database className="size-4" />
                <div>
                  <div className="font-medium">فقط دیتابیس</div>
                  <div className="text-[10px] text-muted-foreground">
                    بدون فایل — فقط dump و schema
                  </div>
                </div>
                {restoreKind === "db_only" && <Check className="size-4 ms-auto" />}
              </button>
            </div>
          </div>

          {accountId && (
            <div className="grid gap-2">
              <Label>{fa.restore.restorePoint}</Label>
              <Select value={pointAsOf} onValueChange={setPointAsOf}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LATEST}>
                    {fa.restore.restorePointLatest}
                    {restorePoints[0] && (
                      <span className="lat-num text-muted-foreground">
                        {" · "}
                        {jalaliDateTimeCompact(restorePoints[0].createdAt)}
                      </span>
                    )}
                  </SelectItem>
                  {restorePoints.map((s) => (
                    <SelectItem key={s.createdAt} value={s.createdAt}>
                      <span className="lat-num">{jalaliDateTimeCompact(s.createdAt)}</span>
                      {s.sizeBytes != null ? ` · ${formatBytes(s.sizeBytes)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {snapshotsQuery.isSuccess && restorePoints.length === 0
                  ? fa.restore.restorePointEmpty
                  : fa.restore.restorePointHint}
              </p>
            </div>
          )}

          {accountId && (
            <div className="grid gap-2 rounded-md border border-border/60 p-3 text-sm">
              <div className="text-xs text-muted-foreground">
                {pointAsOf === LATEST
                  ? `${fa.restore.autoRecommend}${
                      restorePoints[0] ? ` · ${jalaliDateTimeCompact(restorePoints[0].createdAt)}` : ""
                    }`
                  : `${fa.restore.restorePoint}: ${jalaliDateTimeCompact(pointAsOf)}`}
              </div>
              {recommendQuery.isLoading ? (
                <Skeleton className="h-12" />
              ) : recommendQuery.isError || !effectiveSnapshotIds() ? (
                <div className="text-destructive text-xs">
                  اسنپ‌شات معتبری برای این اکانت پیدا نشد.
                </div>
              ) : (
                <RecommendSummary data={effectiveSnapshotIds()!} />
              )}
            </div>
          )}

          <div className="grid gap-2">
            <Label>{fa.restore.pickType}</Label>
            <Select value={targetType} onValueChange={(v) => setTargetType(v as never)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="download_link">{fa.restore.targetDownload}</SelectItem>
                <SelectItem value="cpanel_homedir">{fa.restore.targetHomedir}</SelectItem>
                <SelectItem value="cpanel_full">{fa.restore.targetFull}</SelectItem>
              </SelectContent>
            </Select>
            {targetType === "cpanel_homedir" && (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                tarball روی همان سرور مبدأ ساخته می‌شود و خودکار در مسیر
                <code dir="ltr"> /home/&lt;user&gt;/_offsite_restore/&lt;ts&gt;/cpmove-&lt;user&gt;.tar.gz</code>
                با مالکیت کاربر کپی می‌شود (مشتری از File Manager / SSH می‌تواند بازش کند).
              </p>
            )}
            {targetType === "cpanel_full" && (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                tarball ابتدا روی سرور مبدأ ساخته، سپس از طریق پنل به سرور هدف منتقل و در نهایت با
                <code> /scripts/restorepkg</code> اعمال می‌شود — همهٔ مراحل خودکار.
                اکانتی با همین نام نباید روی سرور هدف وجود داشته باشد.
              </p>
            )}
          </div>

          {targetType === "cpanel_full" && (
            <div className="grid gap-2">
              <Label>{fa.restore.targetServer}</Label>
              <Select value={targetServerId} onValueChange={setTargetServerId}>
                <SelectTrigger><SelectValue placeholder="انتخاب سرور" /></SelectTrigger>
                <SelectContent>
                  {(serversQuery.data?.items ?? []).map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.id}
                      disabled={!s.enabled}
                    >
                      {s.name}{!s.online ? " (آفلاین)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {targetType !== "download_link" && (
            <div className="grid gap-2">
              <Label>کاربر هدف (اختیاری — پیش‌فرض همان کاربر مبدأ)</Label>
              <Input
                dir="ltr"
                value={targetUser}
                onChange={(e) => setTargetUser(e.target.value.trim())}
                placeholder={selectedAccountCache?.user ?? ""}
                className="lat-num"
              />
            </div>
          )}

          {targetType === "download_link" && (
            <div className="grid gap-2">
              <Label>{fa.restore.expiresIn}</Label>
              <Input
                type="number"
                min={1}
                max={168}
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value))}
                className="lat-num"
                dir="ltr"
              />
              {/* Download counter (maxConsumes/consumedCount) was removed
                  intentionally. Files are now served from the offback host's
                  vhost via PHP — the panel never sees the actual byte stream
                  and so could not increment a per-link consumed counter
                  reliably. The expiry timestamp + 3-day default is the only
                  enforcement; the panel's GC tick + offback's hourly cleanup
                  cron both delete artifacts past `expiresAt`. */}
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button
              type="submit"
              disabled={!accountId || !effectiveSnapshotIds() || create.isPending}
            >
              {fa.restore.submit}
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {fa.common.cancel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Combobox-style account picker with server filter, domain-aware search,
 * and an automatic "only-accounts-with-backups" filter so the restore
 * modal can't propose an account that has nothing to restore.
 *
 * The popover closes on outside-click and on selection (the inline list
 * version we had before stayed open forever and let domain-search results
 * accumulate; this is the explicit fix the user asked for).
 */
function AccountCombobox({
  servers,
  selectedAccountId,
  selectedAccountCache,
  snapshotKind,
  onSelect,
}: {
  servers: import("@/lib/services").ServerRow[];
  selectedAccountId: string;
  selectedAccountCache: AccountRow | null;
  snapshotKind: "files" | "db";
  onSelect: (a: AccountRow) => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [serverFilter, setServerFilter] = useState<string>("__all__");
  const [q, setQ] = useState("");
  // Off by default; when on, the picker also lists accounts removed from
  // cPanel that still have snapshots (recover-a-deleted-site flow).
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click. Radix Popover would handle this, but
  // it has known z-index conflicts inside a Dialog — using a portal-free
  // floating panel with manual outside-click detection is simpler and
  // more reliable here.
  useEffect(() => {
    if (!popoverOpen) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [popoverOpen]);

  const accountsQuery = useQuery({
    queryKey: ["accounts-picker", q, serverFilter, snapshotKind, includeDeleted],
    queryFn: () =>
      AccountsApi.list({
        q: q || undefined,
        serverId: serverFilter !== "__all__" ? serverFilter : undefined,
        hasSnapshots: true,
        snapshotKind,
        includeDeleted,
        limit: 50,
        offset: 0,
      }),
    enabled: popoverOpen,
  });

  const display = selectedAccountCache
    ? `${selectedAccountCache.user}${
        selectedAccountCache.primaryDomain ? ` · ${selectedAccountCache.primaryDomain}` : ""
      } — ${selectedAccountCache.serverName}`
    : "انتخاب اکانت…";

  const rows = accountsQuery.data?.items ?? [];

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setPopoverOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          className={
            "min-w-0 truncate text-start " +
            (selectedAccountId ? "" : "text-muted-foreground")
          }
        >
          {display}
        </span>
        <ChevronsUpDown className="size-4 text-muted-foreground shrink-0" />
      </button>

      {popoverOpen && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover text-popover-foreground shadow-md"
          // High z-index keeps us above the Dialog overlay.
        >
          <div className="border-b border-border/40 p-2 grid gap-2">
            <Select value={serverFilter} onValueChange={setServerFilter}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="فیلتر بر اساس سرور" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">همهٔ سرورها</SelectItem>
                {servers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="size-3.5 absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="جستجوی کاربر، دامنه یا ایمیل…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-8 text-sm pe-7"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none px-0.5">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="size-3.5 accent-primary"
              />
              نمایش اکانت‌های حذف‌شده (دارای بکاپ)
            </label>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {accountsQuery.isLoading ? (
              <div className="p-3 text-xs text-muted-foreground text-center">در حال بارگذاری…</div>
            ) : rows.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground text-center">
                {q || serverFilter !== "__all__"
                  ? "اکانتی با اسنپ‌شات منطبق با فیلتر پیدا نشد"
                  : "هیچ اکانتی هنوز بکاپ ندارد"}
              </div>
            ) : (
              rows.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onSelect(a);
                    setPopoverOpen(false);
                  }}
                  className={
                    "flex w-full items-center gap-2 px-3 py-2 text-sm text-start border-b border-border/30 last:border-b-0 hover:bg-muted/40 transition-colors " +
                    (a.id === selectedAccountId ? "bg-primary/10 text-primary" : "")
                  }
                >
                  <Check
                    className={
                      "size-3.5 shrink-0 " +
                      (a.id === selectedAccountId ? "opacity-100" : "opacity-0")
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="lat-num font-medium truncate">{a.user}</span>
                      {a.commercial && (
                        <Badge variant="default" className="text-[9px]">تجاری</Badge>
                      )}
                      {a.deleted && (
                        <Badge variant="destructive" className="text-[9px]">حذف‌شده</Badge>
                      )}
                    </div>
                    {a.primaryDomain && (
                      <div className="lat-num text-[11px] text-muted-foreground truncate" dir="ltr">
                        {a.primaryDomain}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {a.serverName}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RecommendSummary({ data }: { data: { files?: string; meta?: string; db_meta?: string; db?: Record<string, string> } }) {
  const dbCount = Object.keys(data.db ?? {}).length;
  const items: Array<{ k: string; v: string }> = [];
  if (data.files) items.push({ k: "homedir (files)", v: data.files.slice(0, 12) + "…" });
  if (data.meta) items.push({ k: "meta", v: data.meta.slice(0, 12) + "…" });
  if (data.db_meta) items.push({ k: "db_meta", v: data.db_meta.slice(0, 12) + "…" });
  return (
    <div className="text-xs space-y-1">
      {items.map((i) => (
        <div key={i.k} className="flex items-center justify-between">
          <span className="text-muted-foreground">{i.k}</span>
          <span className="font-mono lat-num">{i.v}</span>
        </div>
      ))}
      {dbCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">دیتابیس‌ها</span>
          <span className="tabular">{num(dbCount)} عدد</span>
        </div>
      )}
      {items.length === 0 && dbCount === 0 && (
        <span className="text-muted-foreground">اسنپ‌شاتی پیدا نشد.</span>
      )}
    </div>
  );
}
